import {Injectable} from '@angular/core';
import {DeviceService} from '@mydocma/core/src/lib/device';
import {isIosDevice} from '@docma-mm-core/utils/utils';
import {getActiveEditable, getFocusedScroller, getScrollableAncestor, ListenerGroup} from '../shared/keyboard-viewport.util';
import {MmKeyboardStateService} from '../keyboard-state.service';

const LOCK_DISABLED_SELECTOR = '.fault-detail-step-content';
const SAME_PANE_SELECTOR = '.right-pane-container, .left-pane-container';

@Injectable({providedIn: 'root'})
export class MmIosKeyboardScrollLockService {

  private listeners = new ListenerGroup();

  constructor(
    private deviceService: DeviceService,
    private keyboardState: MmKeyboardStateService,
  ) {}

  init(): void {
    if (this.listeners.active || typeof document === 'undefined') {
      return;
    }
    // background scroll-lock while editing: iOS and Android tablets both need it
    if (!isIosDevice() && !this.deviceService.isTablet()) {
      return;
    }
    this.listeners.add(document, 'touchmove', (event) => this.onTouchMove(event as TouchEvent), {passive: false, capture: true});
  }

  private onTouchMove(event: TouchEvent): void {
    const active = getActiveEditable();
    if (!active) {
      return;
    }
    // disable list only applies while the keyboard is closed — with it open the docked fault
    // form needs the lock too (phones dock the pane, no .cdk-overlay-pane around it)
    if (!this.keyboardState.isKeyboardOpen && !active.closest('.cdk-overlay-pane') && active.closest(LOCK_DISABLED_SELECTOR)) {
      return;
    }
    const target = event.target as Node | null;
    if (target && active.contains(target)) {
      return;
    }
    // scrollable regions that are not ancestors of the focused input are still legitimate scroll
    // targets when they belong to an overlay (dropdown panels, picker dialogs) or to the same pane
    // as the input (structure pane: search field + tree are siblings). Everything else — background
    // page, other panes, non-scrollable regions — stays blocked.
    if (target instanceof Element) {
      const targetScroller = getScrollableAncestor(target as HTMLElement);
      if (targetScroller) {
        // ng-select appends its panel to <body>, so it sits outside every overlay container — measured
        // on tablet: the options list is scrollable (sh 720 / ch 240) but the gesture was blocked here.
        const pane = active.closest(SAME_PANE_SELECTOR);
        if (targetScroller.closest('.cdk-overlay-container, .ng-dropdown-panel') || (pane && pane.contains(targetScroller))) {
          targetScroller.style.overscrollBehavior = 'contain';
          return;
        }
      }
    }
    const scroller = getFocusedScroller(active);
    // document fallback = the field has no scrollable ancestor: nothing legitimate to scroll while
    // typing, and letting the gesture through pans the visual viewport / document out of bounds
    if (scroller === document.scrollingElement || scroller === document.documentElement) {
      if (this.keyboardState.isKeyboardOpen) {
        event.preventDefault();
      }
      return;
    }
    scroller.style.overscrollBehavior = 'contain';
    if (target && scroller.contains(target)) {
      return;
    }
    event.preventDefault();
  }
}
